/**
 * Numeric guards shared by per-LOC `transformToApi` builders.
 *
 * Canonical home (moved from the plan-designer definitions layer) so the
 * transforms stay reusable outside the React grid.
 */

/** Valid when present and >= 0 (dollar amounts). */
export const isValidAmount = (value: number | undefined | null): boolean =>
  value !== undefined && value !== null && value >= 0;

/** Valid when present and >= 1 (months/days/years counts). */
export const isValidCount = (value: number | undefined | null): boolean =>
  value !== undefined && value !== null && value >= 1;

/**
 * Builds a `{ type, [amountKey]?: amount }` object for type+amount benefit fields.
 * Returns undefined (skip the field) if amount is required but not a valid amount.
 * Omits amount from the result when not supported or invalid.
 * `amountSupported` defaults to `amountRequired` for fields where both conditions
 * are the same. Amounts must be 0 or greater.
 */
export const buildConditionalTypeAmount = <T, K extends string>(
  type: T,
  amount: number | null | undefined,
  amountKey: K,
  amountRequired: boolean,
  amountSupported: boolean = amountRequired,
): ({ type: T } & Partial<Record<K, number>>) | undefined => {
  const hasValidAmount = amount !== undefined && amount !== null && amount >= 0;
  if (amountRequired && !hasValidAmount) return undefined;
  return {
    type,
    ...(amountSupported && hasValidAmount ? { [amountKey]: amount } : {}),
  } as { type: T } & Partial<Record<K, number>>;
};
